<template>
    <transition
        name="translateY"
        mode="out-in"
    >
        <ul
            v-show="isOptionsOpen"
            :aria-activedescendant="items[value]"
            class="InputOptions-options m-0 p-0"
            :style="{ maxHeight, overflowY: maxHeight ? 'auto' : undefined }"
            role="listbox"
            tabIndex="-1"
        >
            <li
                v-for="(item, index) in items"
                :key="`input_options_${index}`"
                :aria-selected="isItemSelected(index)"
                :class="[
                    isItemSelected(index) ? `js-input-option-active-${inputId} InputOptions-option--active` : '',
                    `js-input-option-${inputId}`
                ]"
                class="InputOptions-option d-flex justify-content-start"
                role="option"
                tabIndex="0"
                @click="itemSelected(index)"
                @keyup.space="itemSelected(index)"
                @keyup.enter="itemSelected(index)"
                @keydown.down.prevent="toFocus(next(index))"
                @keydown.up.prevent="toFocus(previous(index))"
            >
                <slot
                    v-if="withSlotItem"
                    :item="item"
                    name="option"
                />
                <template v-else>
                    {{ optionsObject ? item.value : item }}
                </template>
            </li>
        </ul>
    </transition>
</template>

<script>
export default {
    props: {
        avoidFocus: {
            type: Boolean,
            default: false
        },
        backgroundColorSelected: {
            type: String,
            default: '#FCE6F2'
        },
        colorSelected: {
            type: String,
            default: '#E6007E'
        },
        isOptionsOpen: {
            type: Boolean,
            default: false
        },
        items: {
            type: [Array, Object],
            required: true
        },
        optionsObject: {
            type: Boolean,
            default: false
        },
        value: {
            type: [Number, String, Array],
            required: true
        },
        withSlotItem: {
            type: Boolean,
            default: false
        },
        maxHeight: {
            type: String,
            default: undefined
        }
    },
    data () {
        return {
            inputId: this._uid,
            itemsHovered: [],
            keys: Object.keys(this.items)
        }
    },
    watch: {
        isOptionsOpen () {
            if (this.isOptionsOpen && !this.value) {
                this.toFocus(this.keys[0])
            } else if (this.isOptionsOpen && !this.avoidFocus) {
                const currentIndex = this.keys.indexOf(`${this.value}`)
                if (currentIndex === -1) {
                    this.toFocus(this.keys[0])
                } else {
                    this.toFocus(this.keys[currentIndex])
                }
            }
        },
        items (val) {
            this.keys = Object.keys(val)
        }
    },
    methods: {
        isItemSelected (index) {
            return this.value && this.value.toString().includes(index)
        },
        next (index) {
            const currentIndex = this.keys.indexOf(`${index}`)
            return currentIndex >= this.keys.length - 1 ? this.keys[0] : this.keys[currentIndex + 1]
        },
        previous (index) {
            const currentIndex = this.keys.indexOf(`${index}`)
            return currentIndex <= 0 ? this.keys[this.keys.length - 1] : this.keys[currentIndex - 1]
        },
        toFocus (index) {
            const currentIndex = this.keys.indexOf(`${index}`)
            this.$nextTick(() => {
                if (currentIndex !== -1) {
                    document.querySelectorAll(`.js-input-option-${this.inputId}`)[currentIndex].focus()
                }
            })
        },
        itemSelected (item) {
            if (Array.isArray(this.value) || item !== this.value) {
                this.$emit('input', item)
            }
        }
    }
}
</script>

<style lang='scss' scoped>
.InputOptions {
    &-options {
        z-index: 3;
        position: absolute;
        top: 100%;
        left: 0;
        width: 100%;
        border-radius: 10px;
        box-shadow: 0 5px 15px rgba(102, 84, 125, .25);
        overflow-x: hidden;
        overflow-y: auto;
        background-color: $color-white;
        list-style: none;
    }

    &-option {
        padding: 8px;
        cursor: pointer;
        transition: background-color .3s ease-in-out, color .3s ease-in-out;
        gap: 10px;

        &:hover, &:focus{
            background-color: #f8f9fa;
            font-weight: bold;
        }

        &--active {
            font-weight: 800;
        }
    }
}
</style>
